import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API, authed } from "@/lib/api";
import { Field, inputCls } from "@/components/admin/SettingsSections";

const toList = (s) => s.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);

// Create (school = null) or edit subdomain + admin emails of a school.
export default function SchoolDialog({ open, onClose, school, rootDomain, onSaved }) {
  const { getToken } = useAuth();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [touchedSlug, setTouchedSlug] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(school?.config.institute_name || "");
    setSlug(school?.slug || "");
    setEmails((school?.admin_emails || []).join("\n"));
    setTouchedSlug(!!school);
  }, [open, school]);

  useEffect(() => {
    if (school || touchedSlug || name.trim().length < 2) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/super/slug-suggest`, { params: { name }, ...(await authed(getToken)) });
        setSlug(data.slug);
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [name, school, touchedSlug, getToken]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const opts = await authed(getToken);
      const body = { slug: slug.trim().toLowerCase() || null, admin_emails: toList(emails) };
      const { data } = school
        ? await axios.patch(`${API}/super/schools/${school.id}`, body, opts)
        : await axios.post(`${API}/super/schools`, { ...body, name }, opts);
      toast.success(school ? "School updated" : `${data.config.institute_name} created`);
      onSaved(data, !school);
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check the details");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="school-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{school ? "Edit school account" : "Add a preschool"}</DialogTitle>
          <DialogDescription>{school ? "Change the web address or who can manage this school." : "A website with sample content is created instantly — the school can edit everything later."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!school && (
            <Field label="School name"><input required minLength={2} className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Happy Kids Play School" data-testid="school-dialog-name-input" /></Field>
          )}
          <Field label="Subdomain">
            <div className="flex items-center rounded-xl border bg-white focus-within:ring-2 ring-brand overflow-hidden">
              <input className="flex-1 px-3 py-2.5 text-sm outline-none min-w-0" value={slug} placeholder="auto-generated"
                onChange={(e) => { setTouchedSlug(true); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")); }} data-testid="school-dialog-slug-input" />
              <span className="px-3 text-sm text-stone-400 bg-stone-50 self-stretch flex items-center">.{rootDomain}</span>
            </div>
            <p className="text-xs text-stone-500 mt-1.5">Also opens at <span className="font-mono">/s/{slug || "…"}</span> on this app.</p>
          </Field>
          <Field label="School admin emails">
            <textarea rows={3} className={inputCls} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="one per line — they sign up with this email to manage this school" data-testid="school-dialog-emails-input" />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border text-sm" data-testid="school-dialog-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="flex items-center gap-2 px-5 py-2 rounded-full btn-brand text-sm font-semibold disabled:opacity-60" data-testid="school-dialog-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {school ? "Save changes" : "Create school"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
